import segmentPlugin from '@analytics/segment'
import {
  AnalyticsActionType,
  AnalyticsPageAction,
  AnalyticsTrackAction,
  DefaultHierarchyLevelType,
  MayBeNull,
  NavigationTree,
  UserDetails,
  HierarchyCustomNodeType,
  HierarchyNode,
  AnalyticsTrackHandler,
  NoCodeAppType,
  DevHubProductType,
  AnalyticsAction,
  AnalyticsEvent,
  AnalyticsPageEvent,
} from '@wpp-open/core'
import Analytics from 'analytics'

import { LaunchLocations } from 'constants/analytics'
import { environment } from 'environment'
import { EventsType, GA4OpenAppAction, GA4PageViewAction } from 'types/ga4'
import { ProjectProcessType } from 'types/projects/project'
import { ProjectCanvasApplicationShort, ProjectPhaseShort, ProjectShort } from 'types/projects/projectNavigation'
import { excludeFalsy, isString, noop } from 'utils/common'
import { getGA4UserId, trackGAAction } from 'utils/ga4Analytics'
import { WorkspaceHierarchyIds } from 'utils/workspace'

declare const window: WindowWithDataLayer

const segmentAppLaunchEvent = 'app_launch'

interface AnalyticsAppTrackProps {
  source?: MayBeNull<string>
  activeHierarchyWithHiddenLevel?: MayBeNull<WorkspaceHierarchyIds>
  navigationTreeWithHiddenLevel?: MayBeNull<NavigationTree>
  productName?: string
  productType?: string
  productOwnerEmail?: MayBeNull<string>
  productCommercialModel?: MayBeNull<string>
  productCategory?: MayBeNull<string>
  productSubcategory?: MayBeNull<string>
  appId?: MayBeNull<string>
  appInstanceId?: string
  appInstanceName?: string
  userDetails: UserDetails
  versionId?: MayBeNull<string>
  versionName?: MayBeNull<string>
  versionType?: MayBeNull<string>
  project?: MayBeNull<ProjectShort<ProjectProcessType>>
  projectPhase?: MayBeNull<ProjectPhaseShort>
  projectItem?: MayBeNull<ProjectCanvasApplicationShort>
  launchedFrom?: MayBeNull<LaunchLocations | string>
  hub_name?: string
  hub_id?: string
  is_system_hub?: boolean
  assignmentType?: MayBeNull<string>
  tenantName?: string
}

export const analytics = Analytics({
  app: 'os-app',
  plugins: [
    !!environment.SEGMENT_ANALYTICS_KEY &&
      segmentPlugin({
        writeKey: environment.SEGMENT_ANALYTICS_KEY,
      }),
  ].filter(excludeFalsy),
})

export const reset = () => {
  analytics.reset()
}

const getAnalyticsTenantAzId = () => analytics?.getState()?.user?.traits?.tenantAzId
const getAnalyticsTenantName = () => analytics?.getState()?.user?.traits?.tenantName

const trackPage = ({ payload }: AnalyticsPageAction, source?: string) => {
  const analyticsTenantAzId = getAnalyticsTenantAzId()
  if (analyticsTenantAzId) {
    return analytics.page(
      {
        title: isString(payload) ? payload : payload.pageTitle,
      },
      {
        agency: analyticsTenantAzId,
        source,
      },
    )
  }
}

const trackAction = ({ payload }: AnalyticsTrackAction, source?: string) => {
  const analyticsTenantAzId = getAnalyticsTenantAzId()
  if (analyticsTenantAzId) {
    if (isString(payload)) {
      return analytics.track(payload, {
        agency: analyticsTenantAzId,
        source,
      })
    } else {
      const { action, params } = payload

      return analytics.track(action, {
        agency: analyticsTenantAzId,
        source,
        ...Object.fromEntries(params.map(({ key, value }) => [key, value])),
      })
    }
  }
}

export const trackAnalytics: AnalyticsTrackHandler = process.env.DEV
  ? noop
  : (action: AnalyticsAction, source) => {
      const userId = window.dataLayer?.find(({ event }) => event === EventsType.Login)?.user_id
      const pagePayload = action.payload as AnalyticsPageEvent
      const analyticsAction = (action.payload as AnalyticsEvent)?.action
      const pageParams = !isString(pagePayload)
        ? pagePayload
        : {
            pageTitle: action.payload,
            pageUrl: window.location.href,
            previousUrl: document.referrer,
          }

      switch (action.type) {
        case AnalyticsActionType.page:
          trackGAAction({
            event: EventsType.Page,
            user_id: userId, // sha256 hash of email
            source,
            ...pageParams,
            workspace_market: undefined,
            workspace_industry: undefined,
            workspace_brand: undefined,
            workspace_client: undefined,
            workspace_region: undefined,
            workspace_custom: undefined,
            context_type: 'os',
            project: undefined,
            tenant_name: getAnalyticsTenantName() || 'unknown',
          } as GA4PageViewAction)
          return trackPage(action, source)

        case AnalyticsActionType.action:
          if (userId && analyticsAction !== segmentAppLaunchEvent) {
            trackGAAction({
              event: EventsType.Segment,
              user_id: userId, // sha256 hash of email
              segment_action_name: typeof action.payload === 'string' ? action.payload : analyticsAction,
              ...((action.payload as AnalyticsEvent)?.params || []).reduce(
                (acc, value) => ({
                  ...acc,
                  [value.key]: value.value,
                }),
                {},
              ),
              source,
            })
          }
          return trackAction(action, source)

        default:
          break
      }
    }

export const trackAppOpenAnalytics = async ({
  source,
  activeHierarchyWithHiddenLevel,
  navigationTreeWithHiddenLevel,
  productName,
  productType,
  productOwnerEmail,
  productCommercialModel,
  productCategory,
  productSubcategory,
  appId,
  appInstanceId,
  appInstanceName,
  userDetails,
  versionId,
  versionName,
  versionType,
  project,
  projectPhase,
  projectItem,
  launchedFrom,
  hub_name,
  hub_id,
  is_system_hub,
  assignmentType,
  tenantName,
}: AnalyticsAppTrackProps) => {
  const activeHierarchyNodes = activeHierarchyWithHiddenLevel?.map(
    nodeId => navigationTreeWithHiddenLevel?.mapping[nodeId],
  )

  const client = activeHierarchyNodes?.find(node => node?.type === DefaultHierarchyLevelType.Client) as HierarchyNode
  const market = activeHierarchyNodes?.find(node => node?.type === DefaultHierarchyLevelType.Market) as HierarchyNode
  const brand = activeHierarchyNodes?.find(node => node?.type === DefaultHierarchyLevelType.Brand) as HierarchyNode
  const industry = activeHierarchyNodes?.find(
    node => node?.type === DefaultHierarchyLevelType.Industry,
  ) as HierarchyNode
  const region = activeHierarchyNodes?.find(node => node?.type === DefaultHierarchyLevelType.Region) as HierarchyNode
  const customs = activeHierarchyNodes?.filter(node => node?.type === HierarchyCustomNodeType)

  const customHierarchyLevel = customs
    ? customs.map(custom => ({
        levelCategory: custom?.customTypeName,
        levelValue: custom?.name,
      }))
    : null

  const data = {
    source: source || '',
    launchedWithContext: !!(client || market || brand || industry || region || customHierarchyLevel?.length),
    market: market?.originalName || '',
    marketAlias: market?.alias || '',
    brand: brand?.originalName || '',
    brandAlias: brand?.alias || '',
    client: client?.originalName || '',
    clientAlias: client?.alias || '',
    industry: industry?.originalName || '',
    industryAlias: industry?.alias || '',
    region: region?.originalName || '',
    regionAlias: region?.alias || '',
    custom: customHierarchyLevel || [],
    productName: productName || '',
    productType: productType || '',
    productOwnerEmail: productOwnerEmail || '',
    productCommercialModel: productCommercialModel || '',
    productCategory: productCategory || '',
    productSubcategory: productSubcategory || '',
    appId: appId || '',
    appInstanceId: appInstanceId || '',
    appInstanceName: appInstanceName || '',
    userId: userDetails.id,
    userEmail: userDetails.email,
    versionId: versionId || '',
    versionName: versionName || '',
    versionType: versionType || '',
    projectId: project?.id || '',
    projectName: project?.name || '',
    projectType: project?.type || '',
    projectProcessType: project?.processType || '',
    phaseId: projectPhase?.id || '',
    phaseName: projectPhase?.name || '',
    taskId: projectItem?.task?.id || '',
    taskName: projectItem?.task?.name || '',
    taskStatus: projectItem?.task?.status || '',
    launchedFrom: launchedFrom || '',
    hub_name: hub_name || '',
    hub_id: hub_id || '',
    is_system_hub: is_system_hub !== undefined ? is_system_hub : '',
  }

  const params = Object.entries(data).map(([key, value]) => ({
    key,
    value,
  }))
  // there are some open app events that are not actually triggered when we want to open an app and, we want to trigger
  // the GA open app event only for actually opening apps
  appId &&
    trackGAAction({
      event: EventsType.AppLaunch,
      user_id: await getGA4UserId(userDetails.email),
      tenant_name: tenantName,
      context_type: assignmentType || undefined,
      product_type: productType
        ? Object.values(NoCodeAppType).includes(productType as NoCodeAppType) ||
          productType === DevHubProductType.NO_CODE_APPLICATION
          ? DevHubProductType.NO_CODE_APPLICATION
          : DevHubProductType.NATIVE_APPLICATION
        : undefined,
      nocode_apptype:
        productType && Object.values(NoCodeAppType).includes(productType as NoCodeAppType) ? productType : undefined,
      product_category: productCategory || undefined,
      product_subcategory: productSubcategory || undefined,
      application_id: appId,
      application_name: appInstanceName || undefined,
      application_source: source || undefined,
      application_launched_from: launchedFrom || undefined,
      application_instance_id: appInstanceId || undefined,
      feature: undefined,
      client: client?.originalName || undefined,
      client_alias: client?.alias || undefined,
      brand: brand?.originalName || undefined,
      brand_alias: brand?.alias || undefined,
      market: market?.originalName || undefined,
      market_alias: market?.alias || undefined,
      industry: industry?.originalName || undefined,
      industry_alias: industry?.alias || undefined,
      region: region?.originalName || undefined,
      regionAlias: region?.alias || undefined,
      project: project?.name || undefined,
    } as GA4OpenAppAction)

  return trackAnalytics({
    type: AnalyticsActionType.action,
    payload: {
      action: segmentAppLaunchEvent,
      params,
    },
  })
}

export const getAnalyticsEventPayload = (event: string, itemName: string) => event.replace('{itemName}', itemName)
